import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { layout, LayoutProps, space, SpaceProps } from "styled-system";
import {
  Box,
  Card,
  Divider,
  LinkButton,
  Link,
  Typography,
} from "@bca/ui-library";

import SaleDateTimeProxyBid from "./SaleDateTimeProxyBid";
import GavelSVG from "./Icons/GavelSVG";
import { buildPreviewLotsUrl } from "../helpers/buildPreviewLotsUrl";
import { formatDateAndWeekday } from "../helpers/date.functions";
import TermsAndConditionsModal from "./TermsAndConditionsModal/TermsAndConditionsModal";
import { StoreType } from "../redux/StoreType";
import {
  GET_APP_STATE,
  SET_IS_SALE_IN_PROGRESS,
} from "../redux/slices/appSlice";

export interface SaleInformationPBProps {
  publishedName: string;
  startDate: string;
  sectionName: string;
  localSaleLocation: string;
  saleLocation?: string;
  saleLocationUrl: string;
  lolCode: string;
  status: string;
  numberOfLots: number;
  vehicleLocationDescription?: string;
}

const getSaleStatus = (startDate: string, saleStatus: string) => {
  const saleTimeUTC = new Date(startDate).toLocaleTimeString("en-GB", {
    timeZone: "UTC",
    hour12: false,
  });
  const saleStartsAtMidnight = saleTimeUTC === "00:00:00";

  if (saleStartsAtMidnight) {
    return "Scheduled";
  } else if (saleStatus === "NotStarted") {
    return "Confirmed";
  } else {
    return "Started";
  }
};

const getSaleDateParam = (saleDateUtc: string) => {
  const date = formatDateAndWeekday(saleDateUtc);

  return date.replace(/,/g, "").replace(/\s/g, "+");
};

const getDaysUntilSale = (startDate: string) => {
  const today = new Date();
  today.setUTCHours(0);
  today.setUTCMinutes(0);
  today.setUTCSeconds(0);
  today.setUTCMilliseconds(0);

  const saleDate = new Date(startDate);
  saleDate.setUTCHours(0);
  saleDate.setUTCMinutes(0);
  saleDate.setUTCSeconds(0);
  saleDate.setUTCMilliseconds(0);

  const timeDifference = saleDate.getTime() - today.getTime();
  const timeDifferenceInDays = Math.floor(timeDifference / 1000 / 60 / 60 / 24);
  return timeDifferenceInDays;
};

const DividerWithLayout = styled(Divider)<LayoutProps>`
  background-color: #7e9ab3;
  ${layout}
`;

const DividerDashed = styled.hr`
  width: 100%;
  border-top: 1px dashed #979797;
  margin: 24px 0 19px 0;
  opacity: 0.3;
`;

interface BlueCardInterface extends LayoutProps, SpaceProps {}

const BlueCard = styled(Card)<BlueCardInterface>`
  border: solid 1px #a2c2da;
  background-color: rgba(33, 150, 243, 0.1);
  ${layout}
  ${space}
`;

const SaleInformationPB: React.FunctionComponent<SaleInformationPBProps> = ({
  publishedName,
  startDate,
  sectionName,
  localSaleLocation,
  saleLocation,
  saleLocationUrl,
  lolCode,
  status,
  numberOfLots,
  vehicleLocationDescription,
}) => {
  const saleIsInProgress = getSaleStatus(startDate, status) === "Started";
  const [showModal, setShowModal] = useState(false);

  const dispatch = useDispatch();

  const {
    app: { isVendor },
  }: StoreType = useSelector(GET_APP_STATE, shallowEqual);

  const handleOpenModal = () => setShowModal(true);

  const handleCloseModal = () => setShowModal(false);

  const oldAuctionsPageProps = {
    link: `${process.env.liveOnlineUrl}deepLink?catalogueRef=${lolCode}`,
    shouldOpenInNewWindow: isVendor,
  };

  const newAuctionsPageProps = {
    onClick: handleOpenModal,
  };

  const getProps = () => {
    if (isVendor) {
      return oldAuctionsPageProps;
    } else {
      return newAuctionsPageProps;
    }
  };

  useEffect(() => {
    if (saleIsInProgress) {
      dispatch(SET_IS_SALE_IN_PROGRESS(true));
    }
  }, [saleIsInProgress]);

  return (
    <Box
      width="50%"
      minWidth={["100%", "50%"]}
      height="100%"
      marginTop={["6px", 0]}
      marginBottom={["25px", "25px", "0px"]}
    >
      <Typography variant="body" color="grey-blue" marginY="1px">
        Featured in
      </Typography>
      <Box display="flex" alignItems="center">
        <GavelSVG
          fill="footer-blue"
          height="18px"
          width="18px"
          mr="8px"
        ></GavelSVG>
        <Typography variant="h5" color="footer-blue" margin={0}>
          {getDaysUntilSale(startDate) >= 7
            ? "Sale name available soon"
            : publishedName}
        </Typography>
      </Box>
      <SaleDateTimeProxyBid
        startDate={startDate}
        status={status}
        saleLocation={saleLocation}
      ></SaleDateTimeProxyBid>
      <LinkButton
        variant="primary-gradient"
        minWidth={["calc(100% - 70px)", 160, 200, 200]}
        paddingLeft={[8, 9]}
        paddingRight={[8, 9]}
        marginRight="18px"
        display={["none", saleIsInProgress ? "inline-flex" : "none"]}
        {...getProps()}
      >
        Join live sale
      </LinkButton>
      {showModal && (
        <TermsAndConditionsModal
          handleCloseModal={handleCloseModal}
          catalogueRef={lolCode}
        />
      )}
      {saleIsInProgress && (
        <BlueCard
          paddingX={6}
          paddingY={5}
          marginX={0}
          marginY={0}
          mr={6}
          width="100%"
          height="auto"
          display={["inline-flex", "none"]}
        >
          <Typography
            variant="body"
            margin={0}
            color="#6f8497"
            css="font-size:15px;"
          >
            To participate in this sale on your mobile, please use the{" "}
            <LinkButton
              variant="tertiary"
              padding={0}
              height={20}
              css="font-size:15px;"
              link="https://www.bca.co.uk/buy/buyer-app"
            >
              Buyer App
            </LinkButton>
          </Typography>
        </BlueCard>
      )}
      <Box maxWidth={["100%", "336px", "350px"]}>
        <DividerDashed></DividerDashed>
      </Box>
      <Box>
        <Typography
          variant="body"
          color="grey-blue"
          marginBottom="12px"
          css="line-height: 1;"
        >
          Within Sale Section
        </Typography>
        <Typography
          variant="h6"
          color="footer-blue"
          marginTop={0}
          marginBottom="6px"
          css="font-weight: bold;"
        >
          {sectionName}
        </Typography>
        <Box display="flex" alignItems="center">
          <Link
            color="primary-light-blue"
            underline="always"
            css={`
              height: 16px;
              font-size: 14px;
              font-weight: 500;
              padding-top: 0;
              padding-bottom: 0;
            `}
            target="_blank"
            href={saleLocationUrl}
            analytics={{
              eventLabel: `vehicle-location|${localSaleLocation}`,
              eventAction: `navigate`,
            }}
          >
            {localSaleLocation}
          </Link>
          <DividerWithLayout
            orientation="vertical"
            width="2px"
            height="15px"
            marginTop={0}
            marginBottom={0}
            marginLeft={3}
            marginRight={3}
          ></DividerWithLayout>
          <Link
            color="primary-light-blue"
            css={`
              height: 16px;
              font-size: 14px;
              font-weight: 500;
              padding-top: 0;
              padding-bottom: 0;
            `}
            underline="always"
            target="_blank"
            analytics={{
              eventLabel: "lot-clicks",
              eventAction: "navigate",
            }}
            href={buildPreviewLotsUrl(
              localSaleLocation,
              sectionName,
              getSaleDateParam(startDate),
              saleLocation
            )}
          >
            {(numberOfLots === null ||
              numberOfLots === undefined ||
              numberOfLots < 0) &&
              "Lots coming soon"}
            {numberOfLots === 1 && `${numberOfLots} lot`}
            {(numberOfLots === 0 || numberOfLots > 1) && `${numberOfLots} lots`}
          </Link>
        </Box>
        {Boolean(vehicleLocationDescription) && (
          <Box display="flex" alignItems="center" marginTop={2}>
            <Typography variant="body-small" color="grey-blue" margin={0}>
              {vehicleLocationDescription}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SaleInformationPB;
